import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Compass, MapPin, Storefront, Trophy, UsersThree, GameController,
  Clock, Heart, NavigationArrow, CalendarDots, Lightning, Star
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MobileDiscoveryPage() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [shopRes, sessRes, tournRes, groupRes, leagueRes] = await Promise.all([
        axios.get(`${API}/api/shop-locator/venue-discovery?limit=6`, { withCredentials: true }),
        axios.get(`${API}/api/sessions?status=open&limit=6`, { withCredentials: true }),
        axios.get(`${API}/api/tournaments?limit=6`, { withCredentials: true }),
        axios.get(`${API}/api/recurring-groups?limit=6`, { withCredentials: true }),
        axios.get(`${API}/api/leagues?limit=6`, { withCredentials: true }),
      ]);
      setShops(shopRes.data);
      setSessions(Array.isArray(sessRes.data) ? sessRes.data : []);
      setTournaments(Array.isArray(tournRes.data) ? tournRes.data : []);
      setGroups(groupRes.data);
      setLeagues(leagueRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const Section = ({ title, icon: Icon, color, viewAll, children }) => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[14px] font-bold text-white flex items-center gap-2">
          <Icon size={16} weight="duotone" style={{ color }} /> {title}
        </h2>
        {viewAll && <button onClick={viewAll} className="text-[11px] text-[#555] hover:text-white transition-colors">View all</button>}
      </div>
      {children}
    </div>
  );

  if (loading) return (
    <div className="max-w-2xl mx-auto space-y-4 p-4" data-testid="discovery-loading">
      {[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-white/[0.03] rounded-lg animate-pulse" />)}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto" data-testid="mobile-discovery-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Discover</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Find games, events, and shops near you</p>
      </div>

      {/* Nearby Shops */}
      <Section title="Nearby Shops" icon={Storefront} color="#10B981" viewAll={() => navigate('/shop-locator')}>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
          {shops.length === 0 ? (
            <p className="text-[12px] text-[#444] py-4 w-full text-center">No shops found</p>
          ) : shops.map(s => (
            <motion.div key={s.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              className="min-w-[200px] bg-[#0c0c14] border border-white/[0.06] rounded-lg p-3 flex-shrink-0 cursor-pointer hover:border-[#10B981]/30 transition-colors"
              onClick={() => navigate(`/shops/${s.id}`)} data-testid={`discover-shop-${s.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <Storefront size={16} weight="duotone" className="text-[#10B981]" />
                <span className="text-[12px] font-semibold text-white truncate">{s.name}</span>
                {s.verified && <Star size={10} weight="fill" className="text-[#F59E0B] flex-shrink-0" />}
              </div>
              <p className="text-[10px] text-[#555] flex items-center gap-1 mb-1.5"><MapPin size={10} /> {s.city}, {s.state}</p>
              <div className="flex gap-1">
                {s.open_for_free_play && <span className="text-[8px] bg-[#3B82F6]/10 text-[#3B82F6] px-1.5 py-0.5 rounded-full">Open Play</span>}
                {s.hosts_tournaments && <span className="text-[8px] bg-[#F59E0B]/10 text-[#F59E0B] px-1.5 py-0.5 rounded-full">Tournaments</span>}
              </div>
              {s.directions && (
                <a href={s.directions.google_maps} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  className="mt-2 flex items-center justify-center gap-1 h-6 rounded bg-[#10B981]/10 border border-[#10B981]/20 text-[#10B981] text-[9px] font-medium hover:bg-[#10B981]/20">
                  <NavigationArrow size={10} /> Directions
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Open Sessions */}
      <Section title="Open Sessions" icon={GameController} color="#9D4CDD" viewAll={() => navigate('/discover')}>
        {sessions.length === 0 ? (
          <p className="text-[12px] text-[#444] py-4 text-center">No open sessions right now</p>
        ) : (
          <div className="space-y-2">
            {sessions.slice(0, 4).map(s => (
              <div key={s.id} className="flex items-center gap-3 bg-[#0c0c14] border border-white/[0.06] rounded-lg px-3 py-2.5 cursor-pointer hover:border-[#9D4CDD]/30 transition-colors"
                onClick={() => navigate(`/sessions/${s.id}`)} data-testid={`discover-session-${s.id}`}>
                <div className="w-8 h-8 rounded-lg bg-[#9D4CDD]/10 flex items-center justify-center flex-shrink-0">
                  <GameController size={14} weight="duotone" className="text-[#9D4CDD]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-white font-medium truncate">{s.title}</p>
                  <p className="text-[10px] text-[#555]">{s.game_type} | {s.location}</p>
                </div>
                <span className="text-[10px] text-[#10B981] font-medium">{s.current_players}/{s.max_players}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Upcoming Tournaments */}
      <Section title="Tournaments" icon={Trophy} color="#F59E0B" viewAll={() => navigate('/tournaments')}>
        {tournaments.length === 0 ? (
          <p className="text-[12px] text-[#444] py-4 text-center">No upcoming tournaments</p>
        ) : (
          <div className="space-y-2">
            {tournaments.slice(0, 3).map(t => (
              <div key={t.id} className="flex items-center gap-3 bg-[#0c0c14] border border-white/[0.06] rounded-lg px-3 py-2.5 cursor-pointer hover:border-[#F59E0B]/30 transition-colors"
                onClick={() => navigate(`/tournaments/${t.id}`)} data-testid={`discover-tournament-${t.id}`}>
                <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
                  <Trophy size={14} weight="duotone" className="text-[#F59E0B]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-white font-medium truncate">{t.name}</p>
                  <p className="text-[10px] text-[#555]">{t.game_type} | {t.date} {t.time}</p>
                </div>
                <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-full font-medium ${
                  t.status === 'registration' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#3B82F6]/10 text-[#3B82F6]'
                }`}>{t.status?.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Groups & Leagues */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Section title="Groups" icon={UsersThree} color="#9D4CDD" viewAll={() => navigate('/groups/recurring')}>
          {groups.length === 0 ? <p className="text-[11px] text-[#444] text-center py-2">None found</p> : groups.slice(0, 3).map(g => (
            <div key={g.id} className="bg-[#0c0c14] border border-white/[0.06] rounded-md px-2.5 py-2 mb-1.5 cursor-pointer hover:border-white/[0.12] transition-colors"
              onClick={() => navigate(`/groups/recurring/${g.id}`)} data-testid={`discover-group-${g.id}`}>
              <p className="text-[11px] text-white font-medium truncate">{g.name}</p>
              <p className="text-[9px] text-[#555]">{g.member_count} members</p>
            </div>
          ))}
        </Section>
        <Section title="Leagues" icon={Trophy} color="#F59E0B" viewAll={() => navigate('/leagues')}>
          {leagues.length === 0 ? <p className="text-[11px] text-[#444] text-center py-2">None found</p> : leagues.slice(0, 3).map(l => (
            <div key={l.id} className="bg-[#0c0c14] border border-white/[0.06] rounded-md px-2.5 py-2 mb-1.5 cursor-pointer hover:border-white/[0.12] transition-colors"
              onClick={() => navigate(`/leagues/${l.id}`)} data-testid={`discover-league-${l.id}`}>
              <p className="text-[11px] text-white font-medium truncate">{l.name}</p>
              <p className="text-[9px] text-[#555]">{l.game_id} | {l.member_count}m</p>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}
